import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { sessionActions } from 'core/session/sessionReducers'
import type { ConnectivityOutcome, DriftOutcome } from './model'

// STORCONN-FR-002: saved storage connectivity outcomes, keyed by `<region>|<host@backend>`
// (see outcomeKey). `results` is persisted so a reload keeps them; `running` is not, so a
// reload in the middle of a check doesn't leave Run disabled forever.
// LUNMAP-FR-003: `drift` holds volume drift outcomes keyed by `<region>|<host id>`; their in
// progress flag shares `running` (with a `drift|` prefix) so it is never persisted either.
export interface StorageConnectivityState {
  results: Record<string, ConnectivityOutcome>
  drift: Record<string, DriftOutcome>
  running: Record<string, true>
}

const initialState: StorageConnectivityState = { results: {}, drift: {}, running: {} }

const {
  name: storageConnectivityKey,
  reducer: storageConnectivityReducer,
  actions: storageConnectivityActions,
} = createSlice({
  name: 'storageConnectivity',
  initialState,
  reducers: {
    started: (state, { payload: key }: PayloadAction<string>) => {
      state.running[key] = true
    },
    finished: (
      state,
      { payload: { key, outcome } }: PayloadAction<{ key: string; outcome: ConnectivityOutcome }>,
    ) => {
      state.results[key] = outcome
      delete state.running[key]
    },
    driftFinished: (
      state,
      { payload: { key, outcome } }: PayloadAction<{ key: string; outcome: DriftOutcome }>,
    ) => {
      state.drift[key] = outcome
      delete state.running[`drift|${key}`]
    },
  },
  // Results describe the cloud, not the user, but don't leave them behind after logout.
  extraReducers: (builder) => {
    builder.addCase(sessionActions.destroySession, () => initialState)
  },
})

export { storageConnectivityActions, storageConnectivityKey }
export default storageConnectivityReducer
